import type { ColumnDef } from '@tanstack/react-table';
import { SETTINGS_PERMISSIONS } from '@/constants/permissions';
import { createActionsColumn, createSerialColumn } from '@/components/common/DataTable';
import { ActiveStatusBadge } from '@/components/common/ActiveStatusBadge';
import { HighlightText } from '@/components/common/HighlightText';
import { RowEditDeleteButtons } from '@/components/common/RowActions';
import { EMPTY_VALUE } from '@/constants/app.constants';
import { formatDisplayDate } from '@/utils/date';
import { formatNumber } from '@/utils/number';
import type { LimitType } from '../types/limitType.types';

interface LimitTypeColumnActions {
  onEdit: (limitType: LimitType) => void;
}

const formatValue = (value: number | null): string =>
  value === null ? EMPTY_VALUE : formatNumber(value);

export function getLimitTypeColumns(
  searchText?: string,
  actions?: LimitTypeColumnActions,
): ColumnDef<LimitType, unknown>[] {
  return [
    createSerialColumn<LimitType>(),
    {
      accessorKey: 'name',
      header: 'Limit Type',
      cell: ({ row }) => (
        <span className="font-medium">
          <HighlightText text={row.original.name} highlightedText={searchText} />
        </span>
      ),
    },
    {
      accessorKey: 'lowValue',
      header: 'Low Value',
      cell: ({ row }) => <span className="tabular-nums">{formatValue(row.original.lowValue)}</span>,
    },
    {
      accessorKey: 'highValue',
      header: 'High Value',
      cell: ({ row }) => (
        <span className="tabular-nums">{formatValue(row.original.highValue)}</span>
      ),
    },
    {
      accessorKey: 'isActive',
      header: 'Status',
      cell: ({ row }) => <ActiveStatusBadge isActive={row.original.isActive} />,
    },
    {
      accessorKey: 'updatedAt',
      header: 'Last Updated',
      cell: ({ row }) => formatDisplayDate(row.original.updatedAt),
    },
    ...(actions
      ? [
          createActionsColumn<LimitType>((limitType) => (
            <RowEditDeleteButtons
              item={limitType}
              itemName={limitType.name}
              onEdit={actions.onEdit}
              editRequires={SETTINGS_PERMISSIONS.LIMIT_TYPES_EDIT}
            />
          )),
        ]
      : []),
  ];
}
